/**
 * useUploadBoardBackground Hook
 *
 * React hook for uploading and managing board background images.
 * Handles the complete upload flow:
 * 1. Get presigned S3 upload URL
 * 2. Upload file to S3
 * 3. Update board background with URL and metadata
 */

import { useState, useCallback } from 'react';
import { useOrchestrator } from './useOrchestrator';
import { Action } from '@/business/types/action.types';
import {
  GetPresignedUploadUrlInput,
  GetPresignedUploadUrlOutput,
  UpdateBoardBackgroundInput,
  UpdateBoardBackgroundOutput,
} from '@/types/generated';
import { useToast } from './useToast';
import { logger, LogCategory } from '@/utils/logger';
import { loggedFetch, FetchTarget } from '@/utils/logged-fetch';

import { BackgroundDisplayMode } from '@/types/BackgroundDisplayMode';
export { BackgroundDisplayMode } from '@/types/BackgroundDisplayMode';

/**
 * Hook state
 */
export interface UseUploadBoardBackgroundState {
  /** Whether an upload is in progress */
  isUploading: boolean;
  /** Upload progress (0-100) */
  uploadProgress: number;
  /** Current upload stage message */
  uploadMessage?: string;
}

/**
 * Hook return type
 */
export interface UseUploadBoardBackgroundReturn extends UseUploadBoardBackgroundState {
  /**
   * Upload a background image for a board
   * @param file - Image file to upload
   * @param boardId - Board ID
   * @param displayMode - Display mode (default: 'cover')
   */
  uploadBackground: (
    file: File,
    boardId: string,
    displayMode?: BackgroundDisplayMode
  ) => Promise<UpdateBoardBackgroundOutput | null>;

  /**
   * Remove the background image from a board
   * @param boardId - Board ID
   */
  removeBackground: (
    boardId: string
  ) => Promise<UpdateBoardBackgroundOutput | null>;
}

/**
 * Maximum file size for background images (5MB)
 */
const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5MB

/**
 * Allowed image MIME types
 */
const ALLOWED_MIME_TYPES = [
  'image/jpeg',
  'image/png',
  'image/webp',
  'image/gif',
];

/**
 * Convert MIME type to backend FileType enum
 * Backend expects: "image" (jpg/jpeg/png/webp) or "gif"
 */
function mimeTypeToFileType(mimeType: string): string {
  if (mimeType === 'image/gif') {
    return 'gif';
  }
  // image/jpeg, image/png, image/webp -> "image"
  if (mimeType.startsWith('image/')) {
    return 'image';
  }
  return 'image'; // default fallback
}

/**
 * Extract image dimensions from file
 */
async function getImageDimensions(
  file: File
): Promise<{ width: number; height: number }> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    const url = URL.createObjectURL(file);

    img.onload = () => {
      URL.revokeObjectURL(url);
      resolve({ width: img.width, height: img.height });
    };

    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error('Failed to load image'));
    };

    img.src = url;
  });
}

/**
 * Hook for uploading board background images
 * @param options - Hook options
 * @returns Upload functions and state
 */
export function useUploadBoardBackground(): UseUploadBoardBackgroundReturn {
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadMessage, setUploadMessage] = useState<string | undefined>();

  const toast = useToast();

  const { execute: getPresignedUrl, isLoading: isGettingUrl } = useOrchestrator<
    GetPresignedUploadUrlInput,
    GetPresignedUploadUrlOutput
  >(Action.GET_PRESIGNED_UPLOAD_URL);

  const { execute: updateBoardBackground, isLoading: isUpdatingBackground } =
    useOrchestrator<UpdateBoardBackgroundInput, UpdateBoardBackgroundOutput>(
      Action.UPDATE_BOARD_BACKGROUND
    );

  /**
   * Upload background image
   */
  const uploadBackground = useCallback(
    async (
      file: File,
      boardId: string,
      displayMode: BackgroundDisplayMode = BackgroundDisplayMode.COVER
    ): Promise<UpdateBoardBackgroundOutput | null> => {
      try {
        setIsUploading(true);
        setUploadProgress(0);
        setUploadMessage('Validating file...');

        // Validate file type
        if (!ALLOWED_MIME_TYPES.includes(file.type)) {
          toast.error('Invalid file type. Please upload a valid image.');
          return null;
        }

        // Validate file size
        if (file.size > MAX_FILE_SIZE) {
          toast.error('File too large. Maximum size is 5MB.');
          return null;
        }

        // Get image dimensions
        setUploadMessage('Reading image...');
        const dimensions = await getImageDimensions(file);
        setUploadProgress(10);

        // Step 1: Get presigned URL
        setUploadMessage('Getting upload URL...');
        const presignedResult = await getPresignedUrl({
          file_name: file.name,
          file_type: mimeTypeToFileType(file.type), // Convert MIME type to backend enum
          bucket_type: 'board_background',
          board_id: boardId, // Use board-scoped path for board backgrounds
        });

        if (!presignedResult.data) {
          toast.error('Failed to get upload URL');
          return null;
        }

        const { presigned_url, absolute_url } = presignedResult.data;
        setUploadProgress(30);

        // Step 2: Upload file to S3
        setUploadMessage('Uploading image...');
        const uploadResponse = await loggedFetch(
          presigned_url,
          {
            method: 'PUT',
            body: file,
            headers: {
              'Content-Type': file.type,
              // ACL is already included in the presigned URL from backend
            },
          },
          // Logger records host + path only (presigned query is a secret)
          {
            category: LogCategory.STORAGE,
            target: FetchTarget.S3_UPLOAD,
          }
        );

        if (!uploadResponse.ok) {
          toast.error('Failed to upload image to S3');
          return null;
        }

        setUploadProgress(70);

        // Step 3: Update board background
        setUploadMessage('Updating board...');
        const updateResult = await updateBoardBackground({
          board_id: boardId,
          background_url: absolute_url,
          display_mode: displayMode,
          dimensions: {
            width: dimensions.width,
            height: dimensions.height,
          },
        });

        if (!updateResult.data) {
          toast.error('Failed to update board background');
          return null;
        }

        setUploadProgress(100);
        setUploadMessage('Complete');
        toast.success('Board background updated successfully');

        return updateResult.data;
      } catch (error) {
        logger.error('Failed to upload board background:', error);
        toast.error('An error occurred during upload');
        return null;
      } finally {
        setIsUploading(false);
        setUploadProgress(0);
        setUploadMessage(undefined);
      }
    },
    [getPresignedUrl, updateBoardBackground, toast]
  );

  /**
   * Remove background image
   */
  const removeBackground = useCallback(
    async (boardId: string): Promise<UpdateBoardBackgroundOutput | null> => {
      try {
        setIsUploading(true);
        setUploadMessage('Removing background...');

        const result = await updateBoardBackground({
          board_id: boardId,
          // Empty background_url signals removal
        });

        if (!result.data) {
          toast.error('Failed to remove board background');
          return null;
        }

        toast.success('Board background removed successfully');
        return result.data;
      } catch (error) {
        logger.error('Failed to remove board background:', error);
        toast.error('An error occurred while removing background');
        return null;
      } finally {
        setIsUploading(false);
        setUploadMessage(undefined);
      }
    },
    [updateBoardBackground, toast]
  );

  return {
    isUploading: isUploading || isGettingUrl || isUpdatingBackground,
    uploadProgress,
    uploadMessage,
    uploadBackground,
    removeBackground,
  };
}
